import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useApp, useAsync } from '../lib/app-context';
import { MediaSlots } from '../components/MediaSlots';
import { Async, Badge, Card, Empty, Field, TagsField, TextField } from '../components/ui';
import {
    DIMENSION_LABELS, MEASUREMENT_DIMENSIONS,
    type Exercise, type MeasurementDimension,
} from '../lib/types';

export function Exercises() {
    const { moduleId } = useParams();
    const { data } = useApp();
    const state = useAsync(() => data.listExercises(moduleId!), [moduleId]);
    const [editing, setEditing] = useState<Exercise | 'new' | null>(null);

    return (
        <>
            <div className="page-head">
                <div>
                    <h1>Exercises</h1>
                    <p className="muted">
                        The movements sessions prescribe. <strong>Measured in</strong> decides which
                        fields the player asks for when a prescription records.
                    </p>
                </div>
                <button className="btn" onClick={() => setEditing('new')}>New exercise</button>
            </div>

            {editing && (
                <div style={{ marginBottom: 20 }}>
                    <ExerciseForm
                        moduleId={moduleId!}
                        exercise={editing === 'new' ? null : editing}
                        onDone={() => { setEditing(null); state.reload(); }}
                        onCancel={() => setEditing(null)}
                    />
                </div>
            )}

            <Async state={state}>
                {(list) => list.length === 0 ? (
                    <Empty title="No exercises yet" hint="Sessions need exercises before they can prescribe anything." />
                ) : (
                    <div className="list">
                        {list.map((exercise) => (
                            <div key={exercise.id} className="list-row">
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div className="title">{exercise.name}</div>
                                    <div className="small muted mono">{exercise.slug}</div>
                                </div>
                                <div className="row-tight">
                                    {exercise.measured_in.map((d) => (
                                        <Badge key={d} tone="teal">{DIMENSION_LABELS[d]}</Badge>
                                    ))}
                                </div>
                                <button className="btn secondary small" onClick={() => setEditing(exercise)}>Edit</button>
                                <DeleteButton
                                    label={exercise.name}
                                    onDelete={() => data.deleteExercise(exercise.id)}
                                    onDone={state.reload}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </Async>
        </>
    );
}

/**
 * Delete with the server's referential-integrity message surfaced verbatim.
 * The FK rejection is a feature — it means the derived link tables caught something the
 * editor would otherwise have broken silently.
 */
export function DeleteButton({ label, onDelete, onDone }: {
    label: string; onDelete: () => Promise<void>; onDone: () => void;
}) {
    const [error, setError] = useState<string | null>(null);

    return (
        <>
            <button
                className="btn danger small"
                onClick={async () => {
                    if (!window.confirm(`Delete “${label}”?`)) return;
                    setError(null);
                    try {
                        await onDelete();
                        onDone();
                    } catch (e) {
                        setError(e instanceof Error ? e.message : String(e));
                    }
                }}
            >Delete</button>
            {error && (
                <div className="badge error" title={error} style={{ maxWidth: 320, overflow: 'hidden' }}>
                    {error}
                </div>
            )}
        </>
    );
}

function ExerciseForm({ moduleId, exercise, onDone, onCancel }: {
    moduleId: string; exercise: Exercise | null; onDone: () => void; onCancel: () => void;
}) {
    const { data } = useApp();
    const [form, setForm] = useState<Omit<Exercise, 'id' | 'module_id'>>({
        slug: exercise?.slug ?? '',
        name: exercise?.name ?? '',
        instructions: exercise?.instructions ?? '',
        tags: exercise?.tags ?? [],
        measured_in: exercise?.measured_in ?? ['repetitions'],
        image_media_id: exercise?.image_media_id ?? null,
        demo_media_id: exercise?.demo_media_id ?? null,
    });
    const [error, setError] = useState<string | null>(null);

    function toggleDimension(dimension: MeasurementDimension) {
        const has = form.measured_in.includes(dimension);
        // Order matters: the first entry is the primary axis for charts and personal bests.
        const next = has
            ? form.measured_in.filter((d) => d !== dimension)
            : [...form.measured_in, dimension];
        setForm({ ...form, measured_in: next.length === 0 ? ['repetitions'] : next });
    }

    async function save() {
        setError(null);
        try {
            await data.upsertExercise({
                ...(exercise ? { id: exercise.id } : {}),
                module_id: moduleId,
                ...form,
                slug: form.slug || form.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
            });
            onDone();
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
    }

    return (
        <Card>
            <h2>{exercise ? 'Edit exercise' : 'New exercise'}</h2>
            <div className="stack" style={{ marginTop: 12 }}>
                {error && <div className="banner error">{error}</div>}
                <div className="field-row">
                    <TextField label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                    <TextField label="Slug" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })}
                        placeholder="derived from name" />
                </div>
                <TextField label="Instructions" multiline value={form.instructions}
                    onChange={(v) => setForm({ ...form, instructions: v })} />
                <TagsField label="Tags" value={form.tags} onChange={(v) => setForm({ ...form, tags: v })} />
                <MediaSlots moduleId={moduleId}
                    imageId={form.image_media_id} videoId={form.demo_media_id}
                    onImage={(id) => setForm({ ...form, image_media_id: id })}
                    onVideo={(id) => setForm({ ...form, demo_media_id: id })} />

                <Field label="Measured in — first is the primary axis">
                    <div className="row-tight" style={{ flexWrap: 'wrap' }}>
                        {MEASUREMENT_DIMENSIONS.map((dimension) => {
                            const index = form.measured_in.indexOf(dimension);
                            const active = index >= 0;
                            return (
                                <button
                                    key={dimension}
                                    type="button"
                                    className={`btn small ${active ? '' : 'secondary'}`}
                                    onClick={() => toggleDimension(dimension)}
                                >
                                    {active && <span style={{ opacity: 0.7 }}>{index + 1}. </span>}
                                    {DIMENSION_LABELS[dimension]}
                                </button>
                            );
                        })}
                    </div>
                </Field>

                <div className="row">
                    <button className="btn" disabled={!form.name} onClick={save}>Save</button>
                    <button className="btn secondary" onClick={onCancel}>Cancel</button>
                </div>
            </div>
        </Card>
    );
}
